import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { FormService } from '../../form.service';
import { arrayLengthValidator } from '../../validators/custom-validators';
import { BaseControlValueAccessorComponent } from '../base-control-value-accessor/base-control-value-accessor.component';
import { FileUploadService } from './file-upload.service';
import { Subject, takeUntil } from 'rxjs';

export interface AllowedFileData {
  readonly extensions: string[];
  readonly maxFileSize: number;
}
interface AllowedExtensionAndSize {
  [extension: string]: number;
}
@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
  ],
})
export class FileUploaderComponent extends BaseControlValueAccessorComponent implements OnInit, OnDestroy {
  @Input() formControl = new UntypedFormControl();
  @Input() numberOfFilesRequired: number | undefined;
  @Input() required = false;
  @Input() allowedFileData: AllowedFileData[];
  @Input() uploadedFileLinks: string[] | undefined = [];
  @Input() id: string;

  allowedExtensionsAndSizes: AllowedExtensionAndSize[];
  filesTooBig: any = [];
  wrongExtensions: any = [];
  uploadedFiles: File[] = [];
  filesIDs: string[] = [];
  private readonly destroy$ = new Subject();

  constructor(
    public formService: FormService,
    private readonly apiService: ApiService,
    private readonly fileUploadService: FileUploadService
  ) {
    super();
  }

  ngOnInit(): void {
    this.uploadedFiles = this.fileUploadService.getFiles(this.id);

    if (this.formControl.value) {
      this.filesIDs = this.formControl.value;
      return;
    }
    this.formControl.setValue([]);
    this.formControl.setValidators(arrayLengthValidator(this.numberOfFilesRequired ?? 0, true));
    this.allowedExtensionsAndSizes = this.allowedFileData.map((item) => {
      return {
        [item.extensions[0]]: item.maxFileSize,
      };
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onFileChange($event: any): void {
    if (!$event.target.files.length || this.formControl.disabled) return;

    const files: File[] = Array.from($event.target.files);

    files.forEach((file, index) => {
      this.isAllowedExtensionAndSize(file, index);

      if (
        this.wrongExtensions[index] ||
        this.filesTooBig[index] ||
        this.uploadedFiles.length >= (this.numberOfFilesRequired ?? 0) ||
        index >= (this.numberOfFilesRequired ?? 0)
      ) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      this.apiService.fileUpload$(formData).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        this.fileUploadService.addFile(this.id, file);
        this.uploadedFiles = this.fileUploadService.getFiles(this.id);
        this.filesIDs.push(res.data.uuid);
        this.formControl.setValue(this.filesIDs);
      });
    });
  }

  fileSize(size: any): string {
    const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(2).toString() + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  }

  deleteSelected(i: any): void {
    if (this.formControl.disabled) {
      return;
    }

    this.fileUploadService.removeFile(this.id, i);
    this.uploadedFiles = this.fileUploadService.getFiles(this.id);
    this.filesIDs.splice(i, 1);
    this.formControl.setValue(this.filesIDs);
  }

  deleteUploadedFile(i: number, uploadedFileLink: string): void {
    this.filesIDs.splice(i, 1);
    this.formControl.setValue(this.filesIDs);
    this.uploadedFileLinks = this.uploadedFileLinks?.filter((link) => link !== uploadedFileLink);
  }

  clickUploader(uploader: HTMLInputElement): void {
    if (this.formControl.disabled) {
      return;
    }

    uploader.click();
    this.wrongExtensions = [];
    this.filesTooBig = [];
  }

  getAllowedExtensionsString(): string {
    return this.allowedFileData.map((item) => item.extensions[0]).join(', ');
  }

  private isAllowedExtensionAndSize(file: File, index: number): void {
    if (!this.allowedExtensionsAndSizes || this.allowedExtensionsAndSizes.length === 0) {
      return;
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileSizeInKB = file.size / 1024;

    const allowedExtensions = this.allowedExtensionsAndSizes.map((allowedExtensionAndSize) => Object.keys(allowedExtensionAndSize)[0]);
    if (!allowedExtensions.includes(fileExtension as string)) {
      this.wrongExtensions[index] = true;
    }
    for (const item of this.allowedExtensionsAndSizes) {
      const extension = Object.keys(item)[0];
      const maxSizeInKB = item[extension] / 1024;

      if (extension === fileExtension && fileSizeInKB <= maxSizeInKB) {
        this.wrongExtensions[index] = false;
        this.filesTooBig[index] = false;
        break;
      }

      if (fileSizeInKB > maxSizeInKB) {
        this.filesTooBig[index] = true;
      }
    }
  }
}
